import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { listJobs, createJob } from '../../services/syncApi';
import { listEpicorJobs, cancelEpicorJob } from '../../services/epicorApi';
import { listEnrichJobs, cancelEnrichJob } from '../../services/pimApi';
import { ENVIRONMENTS, ENVIRONMENT_LABELS } from '../../constants/markets';
import {
  PageHeader,
  Tabs,
  TabPanel,
  DataTable,
  StatusBadge,
  Badge,
  Button,
  EmptyState,
} from '../ui';

// Tabs are split by queue/worker, not by destination system. The Epicor queue
// also runs the customer/address pushes *into* BigCommerce (epicor_push_*), so
// the labels spell out what each queue covers to avoid the "wrong way around"
// confusion (a BC address push is an Epicor-queue job).
const TABS = [
  { id: 'bigcommerce', label: 'Catalog (BigCommerce)' },
  { id: 'epicor', label: 'Customers & Parts (Epicor)' },
  { id: 'pim-enrich', label: 'PIM Enrichment' },
];

const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const num = (v) => (typeof v === 'number' ? v.toLocaleString() : null);

// `processed/eligible (NN%)` tail for push jobs; percent is capped because
// `processed` can exceed `eligible` on retries. Returns '' when no total yet.
const processedTail = (c) => {
  if (typeof c.eligible !== 'number' || c.eligible <= 0) return '';
  const pct = Math.min(100, Math.round(((Number(c.processed) || 0) / c.eligible) * 100));
  return `${num(Number(c.processed) || 0)}/${num(c.eligible)} (${pct}%)`;
};

// Summarize Epicor job counters for the list. Handles the B2B company push
// (companies_*), the B2B address push (addresses_*), and the pull pipelines
// (fetched/upserted/marked_absent).
const epicorProgressSummary = (counters) => {
  const c = counters || {};
  const parts = [];
  if (
    c.companies_created != null ||
    c.companies_updated != null ||
    c.companies_deactivated != null ||
    c.skipped_closed != null
  ) {
    if (num(c.companies_created) != null) parts.push(`${num(c.companies_created)} created`);
    if (num(c.companies_updated) != null) parts.push(`${num(c.companies_updated)} updated`);
    if (c.companies_deactivated) parts.push(`${num(c.companies_deactivated)} deactivated`);
    if (c.skipped_closed) parts.push(`${num(c.skipped_closed)} skipped`);
    if (c.synthesized_emails) parts.push(`${num(c.synthesized_emails)} synth-email`);
    const errors = (c.errors_create || 0) + (c.errors_update || 0) + (c.errors_status || 0);
    if (errors) parts.push(`${num(errors)} errors`);
    const tail = processedTail(c);
    if (tail) parts.push(tail);
  } else if (
    c.addresses_created != null ||
    c.addresses_updated != null ||
    c.addresses_unchanged != null ||
    c.addresses_skipped != null ||
    c.state_dropped != null ||
    c.errors_address != null
  ) {
    if (num(c.addresses_created) != null) parts.push(`${num(c.addresses_created)} created`);
    if (num(c.addresses_updated) != null) parts.push(`${num(c.addresses_updated)} updated`);
    if (num(c.addresses_unchanged) != null) parts.push(`${num(c.addresses_unchanged)} unchanged`);
    if (c.addresses_skipped) parts.push(`${num(c.addresses_skipped)} skipped`);
    if (c.state_dropped) parts.push(`${num(c.state_dropped)} state-dropped`);
    if (c.errors_address) parts.push(`${num(c.errors_address)} errors`);
    const tail = processedTail(c);
    if (tail) parts.push(tail);
  } else {
    if (num(c.fetched ?? c.customers_fetched) != null) parts.push(`${num(c.fetched ?? c.customers_fetched)} fetched`);
    if (num(c.upserted ?? c.customers_upserted) != null) parts.push(`${num(c.upserted ?? c.customers_upserted)} upserted`);
    if (num(c.shiptos_upserted) != null) parts.push(`${num(c.shiptos_upserted)} ship-tos`);
    // Inventory pipeline (epicor_sync_inventory) change-detection counters.
    if (num(c.updated)) parts.push(`${num(c.updated)} changed`);
    if (num(c.history_rows)) parts.push(`${num(c.history_rows)} history rows`);
    if (num(c.marked_absent)) parts.push(`${num(c.marked_absent)} absent`);
    if (num(c.demand_updated)) parts.push(`${num(c.demand_updated)} demand updates`);
    // Warehouse master refresh (epicor_sync_warehouses / inventory syncs).
    if (num(c.warehouses)) parts.push(`${num(c.warehouses)} warehouses`);
    // Archival sales-orders pipeline.
    if (num(c.rollups_refreshed)) parts.push(`${num(c.rollups_refreshed)} rollups`);
    if (num(c.sales_orders_fetched)) parts.push(`${num(c.sales_orders_fetched)} orders`);
    if (num(c.invc_lines_fetched)) parts.push(`${num(c.invc_lines_fetched)} inv lines`);
  }
  return parts.length ? parts.join(' · ') : '—';
};

const enrichProgressSummary = (counters) => {
  const c = counters || {};
  const parts = [];
  if (typeof c.eligible === 'number') parts.push(`${num(c.eligible)} eligible`);
  if (typeof c.enriched === 'number') parts.push(`${num(c.enriched)} enriched`);
  if (typeof c.skipped === 'number' && c.skipped) parts.push(`${num(c.skipped)} skipped`);
  if (typeof c.errors === 'number' && c.errors) parts.push(`${num(c.errors)} errors`);
  if (c.current_sku) parts.push(String(c.current_sku));
  const tail = processedTail(c);
  if (tail) parts.push(tail);
  return parts.length ? parts.join(' · ') : '—';
};

/* ── BigCommerce queue ── */
const BigCommerceJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [storeFilter, setStoreFilter] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [enqueuing, setEnqueuing] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (storeFilter) params.store_code = storeFilter;
      if (environmentFilter) params.environment = environmentFilter;
      if (statusFilter) params.status = statusFilter;
      setJobs(await listJobs(params));
    } finally {
      setLoading(false);
    }
  }, [storeFilter, environmentFilter, statusFilter]);

  useEffect(() => {
    setLoading(true);
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const quickCompare = async (storeCode) => {
    setEnqueuing(true);
    try {
      await createJob({ store_code: storeCode, environment: environmentFilter || 'prod', mode: 'compare', force_fresh: false });
      load();
    } catch (e) {
      alert(`Enqueue failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setEnqueuing(false);
    }
  };

  return (
    <>
      <div className="filters">
        <label>Store
          <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)}>
            <option value="">All</option>
            <option value="AU">AU</option>
            <option value="NZ">NZ</option>
          </select>
        </label>
        <label>Environment
          <select value={environmentFilter} onChange={(e) => setEnvironmentFilter(e.target.value)}>
            <option value="">All</option>
            {ENVIRONMENTS.map((env) => (
              <option key={env} value={env}>{ENVIRONMENT_LABELS[env] || env}</option>
            ))}
          </select>
        </label>
        <label>Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="pending">pending</option>
            <option value="running">running</option>
            <option value="succeeded">succeeded</option>
            <option value="failed">failed</option>
            <option value="cancelled">cancelled</option>
          </select>
        </label>
        <Button variant="secondary" size="sm" disabled={enqueuing} onClick={() => quickCompare('AU')}>
          Quick compare AU
        </Button>
        <Button variant="secondary" size="sm" disabled={enqueuing} onClick={() => quickCompare('NZ')}>
          Quick compare NZ
        </Button>
      </div>

      <DataTable
        caption="BigCommerce sync jobs"
        loading={loading}
        rows={jobs}
        rowKey={(j) => j.id}
        onRowClick={(j) => navigate(`/jobs/history/${j.id}`)}
        empty={
          <EmptyState
            title="No BigCommerce jobs"
            body="No jobs match the current filters. Trigger one from Run Sync."
            action={<Button to="/jobs/run" size="sm">Run a sync</Button>}
          />
        }
        columns={[
          { key: 'id', header: 'ID', render: (j) => <Link to={`/jobs/history/${j.id}`} onClick={(e) => e.stopPropagation()}>#{j.id}</Link> },
          { key: 'store_code', header: 'Store', render: (j) => <Badge variant="primary">{j.store_code}</Badge> },
          { key: 'environment', header: 'Env', render: (j) => <Badge variant={j.environment === 'staging' ? 'warning' : 'neutral'}>{j.environment || 'prod'}</Badge> },
          { key: 'mode', header: 'Mode', render: (j) => <>{j.mode}{j.dry_run && ' (dry)'}</> },
          { key: 'stages', header: 'Stages', render: (j) => <small>{j.stages_completed?.join(', ') || '—'}</small> },
          { key: 'status', header: 'Status', render: (j) => <StatusBadge status={j.status} /> },
          {
            key: 'counters',
            header: 'Counters',
            render: (j) => (
              <small>{j.counters ? Object.entries(j.counters).map(([k, v]) => `${k}=${v}`).join(', ') : '—'}</small>
            ),
          },
          { key: 'created_by', header: 'Created by' },
          { key: 'created_at', header: 'Created', render: (j) => fmtTime(j.created_at) },
          { key: 'finished_at', header: 'Finished', render: (j) => fmtTime(j.finished_at) },
        ]}
      />
    </>
  );
};

/* ── Epicor queue ── */
const EpicorJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setJobs((await listEpicorJobs({ limit: 50 })) || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const requestCancel = async (id) => {
    if (!window.confirm(`Request cancel for Epicor job #${id}?`)) return;
    try {
      await cancelEpicorJob(id);
      load();
    } catch (e) {
      alert(`Cancel failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <DataTable
      caption="Epicor sync jobs"
      loading={loading}
      rows={jobs}
      rowKey={(j) => j.id}
      empty={
        <EmptyState
          title="No Epicor jobs"
          body="Nothing has been pulled from Epicor yet. Trigger a sync from Run Sync."
          action={<Button to="/jobs/run/epicor" size="sm">Run Epicor sync</Button>}
        />
      }
      columns={[
        { key: 'id', header: 'ID', render: (j) => <>#{j.id}</> },
        { key: 'status', header: 'Status', render: (j) => <StatusBadge status={j.status} /> },
        {
          key: 'stage',
          header: 'Stage',
          render: (j) => <small>{(j.stage_filter || []).join(', ') || '—'}{j.payload?.environment ? ` → ${j.payload.environment}` : ''}</small>,
        },
        {
          key: 'companies',
          header: 'Companies',
          render: (j) => (Array.isArray(j.payload?.companies) ? j.payload.companies.join(', ') : '—'),
        },
        { key: 'dry_run', header: 'Dry run', render: (j) => (j.dry_run ? 'yes' : 'no') },
        { key: 'progress', header: 'Progress', render: (j) => <small>{epicorProgressSummary(j.counters)}</small> },
        { key: 'created_by', header: 'Created by' },
        { key: 'finished_at', header: 'Finished', render: (j) => fmtTime(j.finished_at) },
        {
          key: 'actions',
          header: '',
          render: (j) =>
            ['pending', 'running'].includes(j.status) ? (
              <Button variant="ghost" size="sm" onClick={() => requestCancel(j.id)}>Cancel</Button>
            ) : null,
        },
      ]}
    />
  );
};

/* ── PIM enrichment queue ── */
const PimEnrichJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await listEnrichJobs({ limit: 50 });
      setJobs(data?.jobs || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const requestCancel = async (id) => {
    if (!window.confirm(`Request cancel for PIM enrich job #${id}?`)) return;
    try {
      await cancelEnrichJob(id);
      load();
    } catch (e) {
      alert(`Cancel failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <DataTable
      caption="PIM enrichment jobs"
      loading={loading}
      rows={jobs}
      rowKey={(j) => j.id}
      empty={
        <EmptyState
          title="No enrichment jobs"
          body="Run enrichment from a product editor or the PIM product list."
          action={<Button to="/pim/products" size="sm">Open PIM products</Button>}
        />
      }
      columns={[
        { key: 'id', header: 'ID', render: (j) => <>#{j.id}</> },
        { key: 'status', header: 'Status', render: (j) => <StatusBadge status={j.status} /> },
        {
          key: 'scope',
          header: 'Scope',
          render: (j) => {
            if (j.skus?.length) return <small>{j.skus.length} SKU(s)</small>;
            if (j.payload?.filter === 'needs_enrichment') {
              return <small>needs enrichment (limit {j.limit_count ?? j.payload?.limit ?? '—'})</small>;
            }
            return '—';
          },
        },
        { key: 'dry_run', header: 'Dry run', render: (j) => (j.dry_run ? 'yes' : 'no') },
        { key: 'progress', header: 'Progress', render: (j) => <small>{enrichProgressSummary(j.counters)}</small> },
        { key: 'created_by', header: 'Created by' },
        { key: 'finished_at', header: 'Finished', render: (j) => fmtTime(j.finished_at) },
        {
          key: 'actions',
          header: '',
          render: (j) =>
            ['pending', 'running'].includes(j.status) ? (
              <Button variant="ghost" size="sm" onClick={() => requestCancel(j.id)}>Cancel</Button>
            ) : null,
        },
      ]}
    />
  );
};

/* ── Unified history ── */
const JobsHistory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queueParam = searchParams.get('queue');
  const queue =
    queueParam === 'epicor' ? 'epicor' : queueParam === 'pim-enrich' ? 'pim-enrich' : 'bigcommerce';

  const onTabChange = (id) => {
    if (id === 'epicor') setSearchParams({ queue: 'epicor' });
    else if (id === 'pim-enrich') setSearchParams({ queue: 'pim-enrich' });
    else setSearchParams({});
  };

  return (
    <div className="jobs-history">
      <PageHeader
        title="Job History"
        sub="Sync and enrichment jobs across BigCommerce, Epicor, and PIM enrichment queues (refreshed every 5 seconds)."
      />
      <Tabs
        tabs={TABS}
        active={queue}
        onChange={onTabChange}
        label="Job queue"
      />
      <TabPanel id="bigcommerce" active={queue}>
        <BigCommerceJobs />
      </TabPanel>
      <TabPanel id="epicor" active={queue}>
        <EpicorJobs />
      </TabPanel>
      <TabPanel id="pim-enrich" active={queue}>
        <PimEnrichJobs />
      </TabPanel>
    </div>
  );
};

export default JobsHistory;
